import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  PutScopeRequestSchema,
  type Engagement,
  type PutScopeRequest,
} from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';

/**
 * Writes a new scope version — the rules of engagement for a test.
 *
 * Validated with the *same* Zod schema the backend uses, so a bad host is reported against
 * the field that caused it rather than coming back as a 400 with a path the form has to
 * re-interpret. The server still re-validates: this is for the person, not for safety.
 */

/** Lists are entered one per line, which is easier to paste into and to review than chips. */
const lines = (value: string): string[] =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

/** `datetime-local` gives local wall time with no zone; the contract wants an instant. */
const toIso = (local: string): string => (local ? new Date(local).toISOString() : '');

const nowLocal = (offsetMinutes = 0): string => {
  const d = new Date(Date.now() + offsetMinutes * 60_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function DefineScopeDialog({
  engagement,
  suggestedHosts = [],
  onClose,
  onSaved,
}: {
  engagement: Engagement;
  /**
   * Hosts to start the form with — an application accepted from discovery (ADR 0008) offers its
   * own. Only a starting point: a person still sets the window, the authorisation and the
   * permits, and an approver signs the scope off before anything is scanned.
   */
  suggestedHosts?: readonly string[];
  onClose: () => void;
  onSaved: (version: number) => void;
}) {
  const queryClient = useQueryClient();

  const [allowedHosts, setAllowedHosts] = useState(suggestedHosts.join('\n'));
  const [seedUrls, setSeedUrls] = useState(suggestedHosts.map((h) => `https://${h}/`).join('\n'));
  const [excludePaths, setExcludePaths] = useState('');
  const [rps, setRps] = useState('10');
  const [duration, setDuration] = useState('60');
  const [startsAt, setStartsAt] = useState(nowLocal());
  const [endsAt, setEndsAt] = useState(nowLocal(7 * 24 * 60));
  const [activeScan, setActiveScan] = useState(false);
  const [fuzzing, setFuzzing] = useState(false);
  const [reference, setReference] = useState('');
  const [authorisedByName, setAuthorisedByName] = useState('');
  const [authorisedAt, setAuthorisedAt] = useState(nowLocal());
  const [issues, setIssues] = useState<Record<string, string>>({});

  const build = (): PutScopeRequest => ({
    targets: {
      allowedHosts: lines(allowedHosts),
      seedUrls: lines(seedUrls),
      excludePaths: lines(excludePaths),
    },
    limits: {
      maxRequestsPerSecond: Number(rps),
      maxDurationMinutes: Number(duration),
    },
    windows: [{ startsAt: toIso(startsAt), endsAt: toIso(endsAt) }],
    permits: { activeScan, fuzzing },
    authorisation: {
      reference: reference.trim(),
      authorisedByName: authorisedByName.trim(),
      authorisedAt: toIso(authorisedAt),
    },
    // A concurrent write becomes a clean 409 rather than silently replacing an approved scope.
    expectedCurrentVersion: engagement.currentScopeVersion ?? 0,
  });

  const save = useMutation({
    mutationFn: () => api.putScope(engagement.id, build()),
    onSuccess: async (scope) => {
      await queryClient.invalidateQueries({ queryKey: ['engagement', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['scope', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['engagements'] });
      onSaved(scope.version);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setIssues({});

    const parsed = PutScopeRequestSchema.safeParse(build());
    if (!parsed.success) {
      // Keyed by the first path segment pair so a message lands next to its field.
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.slice(0, 2).join('.');
        next[key] ??= issue.message;
      }
      setIssues(next);
      return;
    }
    save.mutate();
  };

  const issue = (key: string) =>
    issues[key] ? (
      <span className="field-error" role="alert">
        {issues[key]}
      </span>
    ) : null;

  const serverError = save.error;

  return (
    <div className="modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="card modal modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scope-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="scope-title" className="panel-title">
          {engagement.currentScopeVersion
            ? `Revise scope (creates v${engagement.currentScopeVersion + 1})`
            : 'Define scope (creates v1)'}
        </h2>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Scope versions are immutable. Saving writes a new version and submits the engagement for
          approval — nothing is tested until a person approves it.
        </p>

        {serverError ? (
          <p className="login-error" role="alert">
            {serverError instanceof ApiError ? serverError.message : 'Could not save the scope.'}
            {serverError instanceof ApiError && serverError.fieldErrors?.length ? (
              <span style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                {serverError.fieldErrors.map((f) => `${f.path}: ${f.message}`).join(' · ')}
              </span>
            ) : null}
          </p>
        ) : null}

        <form onSubmit={submit} className="modal-form">
          <fieldset className="scope-group">
            <legend>Targets</legend>

            {suggestedHosts.length > 0 ? (
              <p className="field-hint">
                Pre-filled with the hosts discovery found for this application. Remove any you are
                not authorised to test.
              </p>
            ) : null}
            <div className="field">
              <label htmlFor="hosts">Allowed hosts — one per line</label>
              <textarea
                id="hosts"
                className="input textarea"
                rows={3}
                required
                placeholder={'juice.guardian-ai.raphael-santiago.com'}
                value={allowedHosts}
                onChange={(e) => setAllowedHosts(e.target.value)}
              />
              <span className="muted field-hint">
                Hostnames only. IP addresses and wildcards are refused, so the metadata endpoint and
                private ranges cannot be named.
              </span>
              {issue('targets.allowedHosts')}
            </div>

            <div className="field">
              <label htmlFor="seeds">Seed URLs — one per line</label>
              <textarea
                id="seeds"
                className="input textarea"
                rows={3}
                required
                placeholder={'https://juice.guardian-ai.raphael-santiago.com/'}
                value={seedUrls}
                onChange={(e) => setSeedUrls(e.target.value)}
              />
              <span className="muted field-hint">Every seed must sit on a host listed above.</span>
              {issue('targets.seedUrls')}
            </div>

            <div className="field">
              <label htmlFor="excludes">Excluded paths — one per line (optional)</label>
              <textarea
                id="excludes"
                className="input textarea"
                rows={2}
                placeholder={'/admin/purge'}
                value={excludePaths}
                onChange={(e) => setExcludePaths(e.target.value)}
              />
              {issue('targets.excludePaths')}
            </div>
          </fieldset>

          <fieldset className="scope-group">
            <legend>Limits and window</legend>
            <div className="field-row">
              <div className="field">
                <label htmlFor="rps">Max requests / second</label>
                <input
                  id="rps"
                  className="input"
                  type="number"
                  min={1}
                  max={50}
                  required
                  value={rps}
                  onChange={(e) => setRps(e.target.value)}
                />
                {issue('limits.maxRequestsPerSecond')}
              </div>
              <div className="field">
                <label htmlFor="duration">Max duration (minutes)</label>
                <input
                  id="duration"
                  className="input"
                  type="number"
                  min={1}
                  max={1440}
                  required
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                />
                {issue('limits.maxDurationMinutes')}
              </div>
            </div>

            <div className="field-row">
              <div className="field">
                <label htmlFor="starts">Testing may start</label>
                <input
                  id="starts"
                  className="input"
                  type="datetime-local"
                  required
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="ends">and must finish by</label>
                <input
                  id="ends"
                  className="input"
                  type="datetime-local"
                  required
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                />
              </div>
            </div>
            {issue('windows.0')}
            {issue('windows')}
          </fieldset>

          <fieldset className="scope-group">
            <legend>Permitted techniques</legend>
            <label className="check">
              <input
                type="checkbox"
                checked={activeScan}
                onChange={(e) => setActiveScan(e.target.checked)}
              />
              <span>
                Active scanning
                <span className="muted field-hint">
                  Sends payloads that can change state. Still requires a separate approval before it
                  runs.
                </span>
              </span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={fuzzing}
                onChange={(e) => setFuzzing(e.target.checked)}
              />
              <span>
                Fuzzing
                <span className="muted field-hint">
                  High request volume. Check with AWS before fuzzing anything hosted there.
                </span>
              </span>
            </label>
          </fieldset>

          <fieldset className="scope-group">
            <legend>Authorisation</legend>
            <div className="field">
              <label htmlFor="ref">Change or CSA reference</label>
              <input
                id="ref"
                className="input"
                required
                maxLength={120}
                placeholder="CSA-4821"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
              {issue('authorisation.reference')}
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="by">Authorised by</label>
                <input
                  id="by"
                  className="input"
                  required
                  maxLength={160}
                  placeholder="A. Reviewer"
                  value={authorisedByName}
                  onChange={(e) => setAuthorisedByName(e.target.value)}
                />
                {issue('authorisation.authorisedByName')}
              </div>
              <div className="field">
                <label htmlFor="at">Authorised on</label>
                <input
                  id="at"
                  className="input"
                  type="datetime-local"
                  required
                  value={authorisedAt}
                  onChange={(e) => setAuthorisedAt(e.target.value)}
                />
                {issue('authorisation.authorisedAt')}
              </div>
            </div>
          </fieldset>

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save scope and submit for approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
